import api from "@/services/api/packages.js";
import { useState } from "react";
import { orderBy, uniqBy } from "lodash";
import { formatStatusOption } from "@/pages/eclip/utils/get-package-status";
import useStaticData from "@/hooks/use-static-data/index";
import { useSelector } from 'react-redux'

const usePackages = () => {
	const user = useSelector(state => state.auth.user)
	const staticData = useStaticData();
	const orgDetail = staticData.orgs;

	const [packageLoading, setPackageLoading] = useState(false);
	const [packageErrorMsg, setPackageErrorMsg] = useState("");
	const [packages, setPackages] = useState([]);
	const [packagesMeta, setPackagesMeta] = useState({});
	const [currentPackage, setCurrentPackage] = useState({});
	const [currentDataElement, setCurrentDataElement] = useState([]);
	const [dataElements, setDataElements] = useState([]);
	const [activityLog, setActivityLog] = useState([]);
	const [filterDataElement, setFilterDataElement] = useState([]);
	const [filterStatus, setFilterStatus] = useState([]);
	const [filterParticipant, setFilterParticipant] = useState([]);
	const [filterCreator, setFilterCreator] = useState([]);
	const [placementList, setPlacementList] = useState([]);
	const [packageCommentsData, setPackageCommentsData] = useState(null);
	const [participantList, setParticipantList] = useState([]);
	const [receivedPackages, setReceivedPackages] = useState([]);
	const [receivedPackagesMeta, setReceivedPackagesMeta] = useState({});
	const [receivedMessages, setReceivedMessages] = useState([]);
	const [receivedMessagesMeta, setReceivedMessagesMeta] = useState({});
	const [dataElementPayload, setDataElementPayload] = useState([]);
	const [dataElementPayloadMeta, setDataElementPayloadMeta] = useState({});

	const NODE_TYPE = {
		package: "PACKAGE",
		milestone: "MILESTONE",
		activity: "ACTIVITY",
	};

	function getParticipantInfo (participantOrgId, infoType){
		const participantInfo = orgDetail.find((org) => org.orgId === participantOrgId);

		if(participantInfo){
			return participantInfo[infoType]
		}

		return participantInfo
	}

	async function fetchAllPackages(params = {}) {
		setPackageLoading(true);

		const [error, response] = await api.getAllPackages(params);

		setPackageLoading(false);

		if (error) {
			return setPackageErrorMsg("Package API has error");
		}

		const { items, meta } = response;

		// Handle response by adding required information for participant field
		const updatedPackageList = items.map((packageItem) => {
			let updatedPackageItem = { ...packageItem };

			const updatedParticipantList = packageItem.participants?.map(
				(participant) => ({
					...participant,
					name: getParticipantInfo(participant.id, 'name'),
				})
			);
			updatedPackageItem.participants = updatedParticipantList;

			return updatedPackageItem;
		});

		setPackages(updatedPackageList);
		setPackagesMeta(meta);
	}

	async function createPackage(payload) {
		setPackageLoading(true);

		const [error, response] = await api.create(payload);

		setPackageLoading(false);

		if (error) {
			return setPackageErrorMsg("Package API has error");
		}

		return response
	}

	async function getPackage(id) {
		setPackageLoading(true);

		const [error, response] = await api.getPackageDetails(id);

		setPackageLoading(false);

		if (error) {
			return setPackageErrorMsg("Package API has error");
		}

		// Handle response by adding additional required information for participant field
		let updatedPackageItem = { ...response };
		const updatedParticipantList = response.participants?.map(
			(participant) => ({
				...participant,
				name: getParticipantInfo (participant.id, 'name'),
				iconUrl: getParticipantInfo(participant.id, 'iconUrl'),
			})
		);
		updatedPackageItem.participants = updatedParticipantList;

		// Track the different placement information (package, milestone, activities)
		const placementItems = [];
		placementItems.push({
			id: response.id,
			name: response.name,
			participants: updatedParticipantList,
			placementType: NODE_TYPE.package,
		});

		const packageMilestones = response.milestones || [];
		if (packageMilestones.length > 0) {
			const updatedMilestonesList = packageMilestones?.map(
				(milestonesItem) => {
					let updatedMilestoneItem = { ...milestonesItem };
					const updatedMilestoneParticipant =
						milestonesItem.participantOrgIds?.map(
							(participantOrgId) => ({
								id: participantOrgId,
								name: getParticipantInfo (participantOrgId, 'name'),
								iconUrl: getParticipantInfo (participantOrgId, 'iconUrl'),
							})
						);

					updatedMilestoneItem.participantOrgIds = updatedMilestoneParticipant;

					placementItems.push({
						id: milestonesItem.id,
						name: milestonesItem.name,
						participants: updatedMilestoneParticipant,
						placementType: NODE_TYPE.milestone,
					});

					const updatedActivityList = milestonesItem.activities?.map(
						(activityItem) => {
							let updatedActivityItem = { ...activityItem };
							const updatedActivityParticipant =
								activityItem.participantOrgIds?.map(
									(participantOrgId) => ({
										id: participantOrgId,
										name: getParticipantInfo(participantOrgId, "name"),
										iconUrl: getParticipantInfo(participantOrgId, "iconUrl"),
									})
								);

							updatedActivityItem.participantOrgIds = updatedActivityParticipant;

							placementItems.push({
								id: activityItem.id,
								name: activityItem.name,
								participants: updatedActivityParticipant,
								placementType: NODE_TYPE.activity,
							});
							return updatedActivityItem;
						}
					);
					updatedMilestoneItem.activities = updatedActivityList;

					return updatedMilestoneItem;
				}
			);
			updatedPackageItem.milestones = updatedMilestonesList;
		}

		setPlacementList(placementItems);
		setCurrentPackage(updatedPackageItem);
	}

	async function updatePackage(id, payload) {
		setPackageLoading(true);

		const [error] = await api.updatePackage(id, payload);

		setPackageLoading(false);

		if (error) {
			return setPackageErrorMsg("Package API has error");
		}
	}

	async function addRemoveDataElements(
		eclipId,
		type,
		dataElements,
		recipientOrgId = null,
	) {
		setPackageLoading(true);
		const [error] = await api.addRemoveDataElements(eclipId, {
			type,
			dataElements,
			recipientOrgId,
		});
		setPackageLoading(false);

		if (error) {
			return setPackageErrorMsg("Package API has error");
		}
	}

	async function getDataElements(params = {}) {
		const [error, response] = await api.listDataElements(params);

		if (error) {
			setDataElements([]);
			return setPackageErrorMsg("Data Elements API has error");
		}

		const formatedDataElementList = response?.map((dataElement) => ({
			id: dataElement.dataElementId,
			name: dataElement.elementName,
			type: dataElement.type,
			$selected: false,
		}));

		setDataElements(formatedDataElementList);
	}

	async function getActivityLog(id) {
		const [error, response] = await api.activityLog(id);

		if (error) {
			setActivityLog([]);
			return setPackageErrorMsg("Package API has error");
		}

		setActivityLog(response);
	}

	async function getPackageComments(packageCommentsId) {
		const [error, packageComments] = await api.getPackageComment(
			packageCommentsId
		);

		if (error) {
			setPackageCommentsData(null);
			return setPackageErrorMsg("Package Comments API has error");
		}

		setPackageCommentsData(packageComments);
	}

	async function postPackageComments(payload) {
		const [error] = await api.updatePackageComment(payload);

		if (error) {
			return setPackageErrorMsg(error.message);
		}

		getPackageComments(payload.nodeId);
	}

	async function getFilterOptions(params = {}) {
		const [error, response] = await api.getFilterOptions(params);

		if (error) {
			return setPackageErrorMsg("Package API has error");
		}

		const { dataElements, status, participant, packageCreators } = response;
		const dataElementFilterList = orderBy(
			dataElements,
			["elementName"],
			["asc"]
		);
		const participantFilterList = orderBy(participant, ["name"], ["asc"]);
		const statusFilterList = status.map((option) => {
			return formatStatusOption(option);
		});
		const updatedStatusFilterList = uniqBy(statusFilterList, "id");
		const creatorFilterList = orderBy(packageCreators, ["name"], ["asc"]);

		setFilterDataElement(dataElementFilterList);
		setFilterStatus(updatedStatusFilterList);
		setFilterParticipant(participantFilterList);
		setFilterCreator(creatorFilterList);
	}

	async function getParticipants(params = {}) {
		const [error, response] = await api.getParticipants(params);

		if (error) {
			setParticipantList([]);
			return setPackageErrorMsg("Package API has error");
		}

		const updatedParticipantList = response
			.filter((recipientId) => recipientId !== user.organizationId)
			.map((participantId) => ({
				id: participantId,
				name: getParticipantInfo(participantId, 'name'),
			}));

		setParticipantList(updatedParticipantList);
	}

	async function importMessageData(eclipId, messageStoreId, payload) {
		setPackageLoading(true)
		const [error, response] = await api.importMessageData(eclipId, messageStoreId, payload)
		setPackageLoading(false)

		if (error) {
			return setPackageErrorMsg(error);
		}
	}

	async function createActivityLog(eclipId, { dataElementId, type }) {
		await api.createActivityLog(eclipId, {
			dataElementId,
			type
		});
	}

	async function sendPackage(id) {
		const [error, _response] = await api.sendPackage(id);

		if (error) {
			return setPackageErrorMsg("Package API has error");
		}
	}

	async function getReceivedPackages(dataElementId, params={}) {
		setPackageLoading(true)
		const [error, response] = await api.getReceivedPackages(dataElementId, params)
		setPackageLoading(false)

		if (error) {
			return setPackageErrorMsg("Package API has error");
		}

		setReceivedPackages(response.items)
		setReceivedPackagesMeta(response.meta)
	}

	async function getReceivedMessages(dataElementId, params={}) {
		setPackageLoading(true)
		const [error, response] = await api.getReceivedMessages(dataElementId, {params});
		setPackageLoading(false)

		if (error) {
			return setPackageErrorMsg("Received Data Element API has error");
		}

		setReceivedMessages(response.items);
		setReceivedMessagesMeta(response.meta);
	}

	async function clearImportData(eclipId, dataElementId, parentNodeType, parentnNodeId) {
		setPackageLoading(true)
		const [error, response] = await api.clearImportData(eclipId, dataElementId, parentNodeType, parentnNodeId);
		setPackageLoading(false)

		if (error) {
			return setPackageErrorMsg("Package API has error");
		}
	}

	async function getDataElementPayload(eclipId, dataElementId, params) {
		const [error, response] = await api.getPackageDEPayload(eclipId, dataElementId, params);

		if (error) {
			return setPackageErrorMsg("Package Payload API has error");
		}

		setDataElementPayload(response.items)
		setDataElementPayloadMeta(response.meta)
	}


	return {
		NODE_TYPE,
		packages,
		packagesMeta,
		currentPackage,
		packageErrorMsg,
		packageLoading,
		dataElements,
		currentDataElement,
		activityLog,
		filterDataElement,
		filterStatus,
		filterParticipant,
		filterCreator,
		placementList,
		packageCommentsData,
		participantList,
		receivedPackages,
		receivedPackagesMeta,
		receivedMessages,
		receivedMessagesMeta,
		dataElementPayload,
		dataElementPayloadMeta,
		setCurrentDataElement,
		setPackageLoading,
		createPackage,
		setDataElements,
		fetchAllPackages,
		getPackage,
		addRemoveDataElements,
		updatePackage,
		getDataElements,
		getActivityLog,
		getFilterOptions,
		getPackageComments,
		postPackageComments,
		getParticipants,
		importMessageData,
		createActivityLog,
		sendPackage,
		getReceivedPackages,
		getReceivedMessages,
		clearImportData,
		getDataElementPayload,
	};
};

export default usePackages;
